import { ref, computed, watch, toRaw } from "vue";
import { defineStore, acceptHMRUpdate } from "pinia";

export const useModalStore = defineStore("modal", () => {
  const modalPayload = ref(null);
  const component = computed(() =>
    modalPayload.value?.component ?? null,
  );
  const props = computed(() => modalPayload.value?.props ?? null);
  const size = computed(() => modalPayload.value?.size ?? null);
  const theme = computed(() => modalPayload.value?.theme ?? null);

  // THE ONE INTERRUPTION (my-account-redesign D9): a payload shown over the
  // open modal, which waits aside and comes back when the interruption
  // settles. A private closure, never store state:
  //   payload — the interrupting payload (raw), as it sits in the slot
  //   covered — the payload it covers (raw), or null when none was open
  //   resolve — settles the promise `interrupt` returned
  // Interruptions never stack: a second one takes the first's place over the
  // same covered payload, so a prompt is never what gets restored.
  let interruption = null;

  function endInterruption(value, { restore }) {
    const ended = interruption;
    interruption = null;
    // Cleared first, so the replacement watch below never reads the restore
    // as someone else taking the slot.
    if (restore) modalPayload.value = ended.covered;
    ended.resolve(value);
  }

  // Show `payload` over the open modal and keep that modal aside, callbacks
  // intact. Resolves with the value `settleInterrupt` passes, or with null
  // when the interruption is closed from the chrome (X, Escape, backdrop),
  // replaced by any other payload, or superseded by a newer interrupt. With
  // no modal open it is a plain open that restores to null.
  function interrupt(payload) {
    let covered = toRaw(modalPayload.value);
    if (interruption) {
      covered = interruption.covered;
      endInterruption(null, { restore: false });
    }
    return new Promise((resolve) => {
      // Recorded before the slot is written, so a callback that settles
      // synchronously finds its interruption.
      interruption = { payload: toRaw(payload), covered, resolve };
      modalPayload.value = payload;
    });
  }

  // End the interruption `payload` started: restore the covered payload
  // exactly, never through closeModal (its afterClose does not run), and
  // resolve `interrupt` with `value`. A no-op once that interruption has
  // ended, so a late callback from a replaced prompt settles nothing newer.
  function settleInterrupt(payload, value = null) {
    if (!interruption || interruption.payload !== toRaw(payload)) return;
    endInterruption(value, { restore: true });
  }

  // Anyone writing another payload (or null) into the slot ends the
  // interruption as a cancel. The writer keeps the slot, and the covered
  // payload is dropped, as any replaced modal is.
  watch(
    modalPayload,
    (current) => {
      if (interruption && toRaw(current) !== interruption.payload) {
        endInterruption(null, { restore: false });
      }
    },
    { flush: "sync" },
  );

  function closeModal() {
    // Closing an interruption from the chrome is its cancel: the covered
    // payload comes back, and no afterClose runs.
    if (interruption) {
      endInterruption(null, { restore: true });
      return;
    }
    if (modalPayload.value?.props?.value?.afterClose) {
      if (modalPayload.value.props.value.afterCloseDestination === "xlModal") {
        modalPayload.value = null;
      } else {
        modalPayload.value = modalPayload.value.props.value.afterClose;
      }
    } else if (modalPayload.value && modalPayload.value.afterClose) {
      if (typeof modalPayload.value.afterClose === "function") {
        console.log("is function");
        modalPayload.value.afterClose();
        modalPayload.value = null;
      } else if (modalPayload.value.afterCloseDestination === "xlModal") {
        modalPayload.value = null;
      } else {
        modalPayload.value = modalPayload.value.afterClose;
      }
    } else {
      modalPayload.value = null;
    }
  }

  const confirmationPayload = ref(null);
  const confirmationTitle = computed(() =>
    confirmationPayload.value?.title ?? null,
  );
  const confirmationMessage = computed(() =>
    confirmationPayload.value?.message ?? null,
  );
  const confirmationAffirmText = computed(() =>
    confirmationPayload.value?.affirmText ?? null,
  );
  // "Cancel" only when the payload names no cancel text at all: a payload's
  // own `null` stands.
  const confirmationCancelText = computed(() => {
    const cancelText = confirmationPayload.value?.cancelText;
    return cancelText === undefined ? "Cancel" : cancelText;
  });
  function closeConfirmation() {
    confirmationPayload.value = null;
  }
  function affirmConfirmation() {
    confirmationPayload.value.accepted = true;
  }

  return {
    confirmationPayload,
    confirmationTitle,
    confirmationMessage,
    confirmationAffirmText,
    confirmationCancelText,
    modalPayload,
    size,
    theme,
    component,
    props,
    closeModal,
    interrupt,
    settleInterrupt,
    closeConfirmation,
    affirmConfirmation,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useModalStore, import.meta.hot));
}
